/* eslint-disable @typescript-eslint/no-use-before-define */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

// project imports
import { useSelector, useDispatch } from 'store';
import { GuardProps } from 'types';
import { setPreviousRoute } from 'store/slices/account';
import { useIdleTimer } from 'react-idle-timer';
import ImdaButton from 'components/button';
import { Box, Stack } from '@mui/material';
import { axiosServicesV3 } from 'utils/services';
import ScreenLoader from 'features/applicationPage/components/ScreenLoader';
import { ExpiryConstants } from 'common/constants';
import { useLogout } from 'hooks/useLogout';
import Modal from 'components/modals/Modal';
import { decryptId } from 'common/decrypt';

// ==============================|| AUTH GUARD ||============================== //

/**
 * Authentication guard for routes
 * @param {PropTypes.node} children children element/node
 */
type ButtonVariant = 'primary' | 'secondary' | 'text';

function AuthGuard({ children }: GuardProps) {
    const { isLoggedIn, user } = useSelector((state) => state.account);
    const [isLoggingOut, setLogout] = useState(false);
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const login = searchParams.get('login');
    const logout = useLogout();

    const handleOnIdle = async () => {
        value.pause();
        setLogout(true);
        const decrypted = await decryptId(user?.id || '', 'decrypt');

        const response = await axiosServicesV3.post('logout/', {
            ID: decrypted
        });
        if (response.status === 200) {
            setLogout(false);
            logout();
        } else {
            setLogout(false);
        }
    };
    const onPrompt = () => {
        setIsOpen(true);
    };

    const onAction = async (event: any) => {
        const expiry = window.localStorage.getItem('imda_expires_in');
        if (expiry) {
            const date: number = +expiry * 1000;

            if (((date - Date.now()) as number) <= 5 * 60 * 1000) {
                const id = window.localStorage.getItem('id');
                if (id) {
                    const response = await axiosServicesV3.post('refresh/', {
                        ID: id
                    });
                    localStorage.setItem('imda_expires_in', response.data);
                }
            }
        }
    };

    const value = useIdleTimer({
        timeout: 1000 * 60 * 5,
        onIdle: handleOnIdle,
        onAction,
        onPrompt,
        promptBeforeIdle: 1000 * 60 * 1,
        debounce: 500,
        crossTab: true,
        leaderElection: true,
        syncTimers: 200
    });
    useEffect(() => {
        if (!isLoggedIn) {
            dispatch(setPreviousRoute(`${location.pathname}${location.search}`));
            if (login) {
                navigate(`/${login}`, { replace: true });
            } else {
                navigate('/login', { replace: true });
            }
        }
    }, [isLoggedIn, location.pathname, navigate]);

    const handleStillHere = () => {
        value.activate();
        setIsOpen(false);
    };
    const handleSignOut = async () => {
        setLogout(true);
        const decrypted = await decryptId(user?.id || '', 'decrypt');

        const response = await axiosServicesV3.post('logout/', {
            ID: decrypted
        });
        if (response.status === 200) {
            setLogout(false);
            logout();
        } else {
            setLogout(false);
        }
    };

    const actionsForm = [
        {
            actionText: 'Sign out now',
            actionID: 'signout',
            actionCallback: handleSignOut,
            actionVariant: 'text' as ButtonVariant
        },
        {
            actionText: 'Stay signed in',
            actionID: 'signedin',
            actionCallback: handleStillHere,
            actionVariant: 'primary' as ButtonVariant
        }
    ];

    return (
        <>
            {isLoggingOut && <ScreenLoader />}
            {children}
            <Modal
                title="Your Session is About to Expire"
                open={isOpen}
                content={
                    <Stack alignItems="center" justifyContent="center">
                        <h4>Do you want to stay signed in?</h4>
                    </Stack>
                }
                setOpen={setIsOpen}
                removeOnBackGroundClose
                removeCloseIcon
                actions={actionsForm}
                customWidth="600px"
            />
        </>
    );
}

export default AuthGuard;
